// Schemas for the Tool primitive (0.1.8+).
//
// A tool is a first-class extension bound to a nest with a dedicated
// storage container holding its registry.yaml. Tools ship with seed
// defaults (tags / rules / file-workflows / work-item workflows /
// custom properties) materialized on install via the same skip-on-
// natural-key idempotency as bundle import.
//
// See docs/spec (forthcoming §15 once it lands) for the full
// conceptual model. apps/api/src/routes/tools.ts is the runtime entry.
import { z } from 'zod';
import { deletedFilterFields, refineDeletedFilterMutualExclusion } from './common.js';
// ── Slug + identifier shapes ────────────────────────────────────
const ToolSlugSchema = z.string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'lowercase letters, digits and hyphens; must start with a letter or digit');
const ToolTypeSchema = z.string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'lowercase letters, digits and hyphens');
// SemVer-ish version string. Permissive — we don't enforce strict
// semver because pre-1.0 tools may use 'dev' / '2026-05-26' / etc.
const ToolVersionSchema = z.string().min(1).max(32);
// ── Manifest sub-schemas ─────────────────────────────────────────
//
// The storage block is the most load-bearing — it ties the tool to a
// single container. On install the handler creates the container if
// missing OR binds an existing one with this name.
//
// Two shapes (DSR 1.2.0):
//   - legacy: `registryPath` — ONE file inside the container holds
//     the tool's canonical data (yaml / json / whatever the tool
//     decides).
//   - multi-file: `registryFiles[]` — one entry per registered file,
//     each with a UI label + a schema key the native page dispatches
//     table rendering on. Takes precedence over registryPath when
//     both are present.
// At least one of the two must be provided (refine below). Use
// resolveRegistryFiles() to read either shape uniformly.
/** One registered registry file inside the tool's bound container.
 *  Schema keys: data-sources-v1 + bookmarks-v1 (DSR), plus the
 *  timeline tool trio (Timeline Lane 1.0.0): events-v1 (deep-time
 *  historical events, BCE-capable dates), audit-timeline-v1
 *  (modern-precision workspace audit log), timeline-marker-v1
 *  (marker/window annotations with a `marker:` render sub-object;
 *  shared by markers.yaml + future-events.yaml). */
export const RegistryFileEntry = z.object({
    path: z.string().min(1).max(256),
    label: z.string().min(1).max(64),
    schema: z.enum([
        'data-sources-v1',
        'bookmarks-v1',
        'events-v1',
        'audit-timeline-v1',
        'timeline-marker-v1',
    ]),
    // CTSK-361 slice B — optional per-file default sort for the native
    // table renderer. `column` names a column key of the file's schema
    // (free-form string — the renderer falls back to its built-in
    // default when the named column doesn't exist); `direction`
    // defaults to 'desc' (most-recent-first is the common case, e.g.
    // Bookmarks `last_captured`). Consumer wiring (Bookmarks table)
    // lands with CTSK-361 slice A via resolveRegistryFileDefaultSort
    // in apps/web/src/lib/registry-file-sort.ts.
    defaultSort: z.object({
        column: z.string().min(1).max(128),
        direction: z.enum(['asc', 'desc']).default('desc'),
    }).optional(),
    // CTSK-351 (folds in CTSK-352) — optional per-file row-action
    // palette for the native table renderer. Each entry declares one
    // button rendered in the table's rightmost "Actions" column; when
    // the array is absent/empty the column hides entirely (no dead
    // column artifact). Ships one shape today — a URL launcher with
    // template interpolation over the row's entry fields — plus a
    // `clipboard:` sub-scheme for copy-to-clipboard actions. Additional
    // verbs (post-invoke, in-app navigation) can extend the shape in a
    // follow-up without breaking today's consumers.
    //
    // urlTemplate variables: {url}, {uuid}, {id}, {title} — resolved
    // from the row's entry and URL-encoded (except the `clipboard:`
    // prefix, which the palette hands to copyToClipboard raw). See
    // apps/web/src/lib/template-interpolate.ts for the interpolator
    // and apps/web/src/components/RegistryRowActionsPalette.tsx for
    // the renderer.
    //
    // requiresField: when set, actions hide on entries missing that
    // field — the palette never renders a button that would produce a
    // broken URL / empty clipboard write. Applied on the row's entry
    // fields (matches the interpolation variable set).
    //
    // openInNewTab is a plain `.optional()` (NOT `.default(true)`) so the
    // z.infer output type stays boolean-or-undefined, matching what a
    // manifest-author actually writes. Runtime treats `undefined` and
    // `true` identically (new-tab is the default UX); only `false` opts
    // out. Keeping the field optional at the type level lets consumers
    // pass literal manifests / fixtures without stamping `true`.
    // CTSK-364 — fetch-and-map mode. `mode` defaults to 'open-in-tab'
    // at READ time (plain .optional(), not .default(): existing
    // manifests round-trip byte-identical through the PATCH /manifest
    // editor, and z.infer stays honest about what authors wrote — the
    // same reasoning as openInNewTab above). When mode='fetch-and-map'
    // the palette fetches the interpolated urlTemplate instead of
    // opening it, optionally POSTing a JSON `bodyTemplate` (values
    // interpolated as NATIVE types — `{tags}` stays an array; see
    // apps/web/src/lib/template-interpolate.ts interpolateBody), then
    // maps response fields onto the initiating entry via `responseMap`
    // (dotted-path in response → dotted-path on entry, e.g.
    // "links.uuid" → "external_refs.hyper_scan.uuid") and persists the
    // patch through PATCH /tools/:id/registry/:filePath/entries/:entryId.
    // `onSuccess` picks the post-fetch UX (default 'toast+refresh').
    rowActions: z.array(z.object({
        slug: z.string().min(1).max(64)
            .regex(/^[a-z0-9][a-z0-9-]*$/, 'lowercase letters/digits/hyphens; must start with a letter or digit'),
        label: z.string().min(1).max(64),
        urlTemplate: z.string().min(1).max(1024),
        openInNewTab: z.boolean().optional(),
        icon: z.string().min(1).max(64).optional(),
        requiresField: z.string().min(1).max(64).optional(),
        // CTSK-364 — round-trip fetch config (all optional; absent = the
        // pre-CTSK-364 open-in-tab behavior, backward-compat guaranteed).
        mode: z.enum(['open-in-tab', 'fetch-and-map']).optional(),
        method: z.enum(['GET', 'POST']).optional(),
        bodyTemplate: z.record(z.string(), z.unknown()).optional(),
        responseMap: z.record(z.string(), z.string().min(1).max(256)).optional(),
        onSuccess: z.enum(['toast', 'refresh', 'toast+refresh']).optional(),
    })).max(20).optional(),
});
const ToolStorageSchema = z.object({
    containerName: z.string().min(1).max(120),
    registryPath: z.string().min(1).max(256).optional(), // legacy: single-file
    registryFiles: z.array(RegistryFileEntry).optional(), // new: multi-file
}).refine((s) => s.registryPath || (s.registryFiles && s.registryFiles.length > 0), {
    message: 'storage must provide registryPath or at least one registryFiles entry',
});
/**
 * Resolve a storage block (either shape) to a uniform file list.
 * Backward-compat rule — server + client both apply it through this
 * one helper: when `registryFiles` is absent/empty but `registryPath`
 * is present, the legacy path is treated as a single 'Registry' file
 * with the data-sources-v1 schema. Non-empty `registryFiles` wins
 * when both are present.
 *
 * Accepts a structural subset of ToolStorage so callers can pass
 * either a parsed manifest storage block or the over-the-wire tool
 * row's `storage` object (where fields may be null).
 */
export function resolveRegistryFiles(storage) {
    if (storage.registryFiles && storage.registryFiles.length > 0)
        return storage.registryFiles;
    if (storage.registryPath) {
        return [{ path: storage.registryPath, label: 'Registry', schema: 'data-sources-v1' }];
    }
    return [];
}
// Defaults block. Each array is the SAME shape as bundle import's
// per-resource items (see admin.ts) — we keep them passthrough here
// so the install handler can run them through the strict bundle
// shapes at apply time. This lets a tool manifest stay forward-
// compatible if bundle shapes grow new optional fields.
//
// Why .passthrough() at the wrapper rather than strict at the item:
// every default-resource has already been audited by bundle's own
// schemas. Re-validating here would just duplicate. Better to let
// the install handler delegate to the bundle-applier directly.
const ToolDefaultsSchema = z.object({
    // CTSK-425 — storage containers this tool needs, auto-applied on
    // install (skip-if-exists by (orgId, name), same idempotency as the
    // other sections). Loose envelope like the siblings: each item is
    // re-validated in the applier against bundle-import's ContainerItem
    // (name, slug?, description?, color?, icon?, isDefault?) — a bad
    // item lands in the response summary's errors[] and is skipped
    // rather than failing the whole install (CTSK-416 discipline).
    // Applied FIRST so defaults.rules[] can reference declared
    // containers by name (containerToAssign / assign_container).
    containers: z.array(z.record(z.unknown())).optional(),
    tags: z.array(z.record(z.unknown())).optional(),
    rules: z.array(z.record(z.unknown())).optional(),
    fileWorkflows: z.array(z.record(z.unknown())).optional(),
    workflows: z.array(z.record(z.unknown())).optional(),
    customProperties: z.array(z.record(z.unknown())).optional(),
});
// Cross-tool read grants. ['*'] = any installed tool in this org
// may GET this tool's registry. An array of tool slugs = ONLY those
// tools may read. Omit = registry is private to this tool's
// container ACL (only this tool's own caller, gated on
// tools:read_registry, may GET it).
const ToolAccessSchema = z.object({
    readableBy: z.array(z.string()).optional(),
});
// ── Tool actions (FR 2026-05-31 — Tool invocation framework) ───
//
// Each entry in `tool.actions[]` declares one button the palette
// can render for this tool. v1 supports two verbs:
//   - 'tab'    : <a target="_blank"> — operator drives the work
//                in the tool's UI; no proxy call required.
//   - 'invoke' : POST /api/v1/tools/:slug/invoke — synchronous
//                HTTP call through the API proxy; result is a
//                ToolInvokeResult (findings + optional documents).
//
// Async ('invoke-async') and async-only transports (queues,
// webhooks) ship in v2.
//
// Inputs are a typed object map (NOT a stringly-typed positional
// list) so the upstream payload field names are stable and the
// manifest can map Captain Tasko-side state to those names.
//
// Returns is an explicit object distinguishing findings (in the
// JSON response) from documents (either pushed back via the
// existing /documents/ingest by the tool itself, OR ingested by
// the proxy on the tool's behalf). 'tool-postback' is the
// existing report_generator pattern; 'proxy-ingest' is the new
// path that lets simpler tools just return a JSON payload of
// ToolProducedDocument objects without knowing about ingest.
// Allowed `inputs[<key>]` selectors. Stringly-typed BUT only these
// known names — the proxy rejects unknown selectors. Each name
// resolves a Captain-Tasko-side piece of state to forward upstream.
const ToolInputSelectorSchema = z.enum([
    'document.id',
    'document.title',
    'document.content',
    'document.frontmatter',
    'document.tags',
    'document.containerId',
    'document.metadata',
    'org.id',
    'org.slug',
    'caller.id',
]);
const ToolActionReturnsSchema = z.object({
    /** When true, the upstream response includes findings[] (parsed
     *  as Finding objects). */
    findings: z.literal(true).optional(),
    /** When set, the upstream may produce documents. 'tool-postback'
     *  means the tool calls /documents/ingest itself (existing
     *  report_generator path). 'proxy-ingest' means the upstream
     *  returns { documents: ToolProducedDocument[] } and the proxy
     *  forwards each to /documents/ingest on the tool's behalf. */
    documents: z.object({
        mode: z.enum(['tool-postback', 'proxy-ingest']),
    }).optional(),
}).refine((v) => Boolean(v.findings) || Boolean(v.documents), { message: 'returns must declare at least one of findings or documents' });
const ToolHttpTransportSchema = z.object({
    type: z.literal('http'),
    /** Full URL (scheme + host + path). The proxy enforces a host
     *  allowlist (default: *.chadwiki.loc + loopback denied) at
     *  invoke time — manifests CANNOT bypass it. */
    endpoint: z.string().url(),
    method: z.enum(['GET', 'POST', 'PUT', 'PATCH']).default('POST'),
    /** Hard timeout for the upstream call, seconds. Caps at 60 for
     *  v1 (sync); anything longer must use invoke-async (v2). */
    timeoutSec: z.number().int().min(1).max(60).default(30),
    /** Reference key into the API server's env for the auth header
     *  (e.g. 'report-gen:apiKey' → process.env.REPORT_GEN_API_KEY).
     *  Tools never receive credentials from manifests directly. */
    authRef: z.string().min(1).max(120).optional(),
    /** Extra HTTP headers to send. Static values only — no
     *  templating in v1. */
    headers: z.record(z.string()).optional(),
});
// `verb: 'tab'` — the simplest action. No proxy call; the UI
// renders <a target="_blank" href={url}>. URL is the only required
// field. `inputs`/`returns` are intentionally absent — there's no
// upstream call to map them onto.
const TabActionSchema = z.object({
    key: z.string().min(1).max(64).regex(/^[a-z0-9][a-z0-9-]*$/, 'lowercase letters/digits/hyphens'),
    label: z.string().min(1).max(64),
    description: z.string().max(500).optional(),
    /** Lucide icon name — the web app maps to a component via its
     *  static icon library. Unknown names render a fallback icon. */
    icon: z.string().min(1).max(64).optional(),
    verb: z.literal('tab'),
    url: z.string().url(),
    /** CTSK-121 — per-action default parameters. For tab actions the
     *  launcher appends these as URL query params (existing query keys
     *  in the manifest URL win). Named `paramDefaults`, NOT `defaults`
     *  — the manifest already has a tool-level `defaults` block for
     *  tags/rules/CPs seeding (CTSK-297) and an action-level `defaults`
     *  would collide semantically. Optional: manifests without it are
     *  untouched. */
    paramDefaults: z.record(z.unknown()).optional(),
    /** Reuses the existing ctsk-115 appliesToTags mechanism — when
     *  set, this action only renders on documents whose tag set
     *  intersects this list. Empty/unset = always applicable. */
    appliesToTags: z.array(z.string()).optional(),
    requiredCaps: z.array(z.string()).optional(),
});
// `verb: 'invoke'` — synchronous HTTP call through the proxy.
const InvokeActionSchema = z.object({
    key: z.string().min(1).max(64).regex(/^[a-z0-9][a-z0-9-]*$/, 'lowercase letters/digits/hyphens'),
    label: z.string().min(1).max(64),
    description: z.string().max(500).optional(),
    icon: z.string().min(1).max(64).optional(),
    verb: z.literal('invoke'),
    transport: ToolHttpTransportSchema,
    /** Typed input mapping — keys are the field names the upstream
     *  payload uses; values are Captain Tasko selectors. */
    inputs: z.record(ToolInputSelectorSchema).default({}),
    /** CTSK-121 — per-action default parameters (depth, timeout,
     *  output format, …). The invoke proxy merges these UNDER any
     *  caller-supplied params (caller wins) so every invocation gets
     *  the manifest author's sensible defaults without the operator
     *  retyping them. Same naming rationale as the tab variant:
     *  `paramDefaults`, never `defaults` (CTSK-297 collision). */
    paramDefaults: z.record(z.unknown()).optional(),
    returns: ToolActionReturnsSchema,
    appliesToTags: z.array(z.string()).optional(),
    /** Capability list stacked on top of the framework-level
     *  `tools:invoke` cap. */
    requiredCaps: z.array(z.string()).optional(),
});
export const ToolActionSchema = z.discriminatedUnion('verb', [
    TabActionSchema,
    InvokeActionSchema,
]);
// ── The manifest envelope ────────────────────────────────────────
//
// What an operator uploads to install a tool. Mirrors the bundle
// envelope's shape ($schema + payload) so the visual + tooling
// stories rhyme.
export const ToolManifestSchema = z.object({
    $schema: z.literal('captain-tasko-tool').optional(),
    tool: z.object({
        slug: ToolSlugSchema,
        name: z.string().min(1).max(200),
        description: z.string().max(2000).nullable().optional(),
        type: ToolTypeSchema,
        version: ToolVersionSchema,
        // CTSK-347 — optional landing destination for clicking the tool
        // from a nest tile / tools list. Two accepted shapes:
        //   - relative in-app route, starting '/' (e.g. '/documents')
        //     → same-tab onNavigate
        //   - absolute http(s) URL → opens in a new tab
        // Unset = existing default behavior (single native tool → its
        // native page, else Settings → Tools filtered by nest; CTSK-348).
        // Persisted as a settings-stash on the tools row (no DB column —
        // same discipline as settings.registryFiles) and rehydrated into
        // GET /tools/:id/manifest. Resolved client-side via
        // resolveToolLandingUrl in apps/web/src/lib/tool-navigation.ts.
        landingPath: z.string().min(1).max(512).refine((v) => v.startsWith('/') || /^https?:\/\//.test(v), { message: 'landingPath must be a relative route (starting /) or absolute URL (http[s]://)' }).optional(),
        storage: ToolStorageSchema,
        defaults: ToolDefaultsSchema.optional(),
        registryAccess: ToolAccessSchema.optional(),
        // FR 2026-05-31 — actions declare what this tool can DO, as
        // rendered by the Tool Palette. Backward-compatible: omit or
        // empty array = the tool has no palette actions (registry-only
        // tools like data-sources-registry are valid in this shape).
        actions: z.array(ToolActionSchema).max(50).default([]),
        // Tool-specific runtime config. Stored on tools.settings jsonb.
        // Manifest authors can ship sensible defaults here that the
        // operator can later override via PATCH /tools/:id.
        settings: z.record(z.unknown()).optional(),
    }),
});
// ── Route bodies ────────────────────────────────────────────────
//
// POST /tools — install. Body: { nestSlug, manifest, dryRun? }.
// nestSlug picks which nest the tool lands in. dryRun runs the
// validate+resolve phase without writing — used by the UI's
// install preview pane to show what'll be created.
export const ToolInstallSchema = z.object({
    nestSlug: z.string().min(1).max(64),
    manifest: ToolManifestSchema,
    dryRun: z.boolean().default(false),
});
// PATCH /tools/:id — partial update. All fields optional. Cap-gated
// on tools:configure (held by owner / operator / emissary).
export const ToolPatchSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    version: ToolVersionSchema.optional(),
    registryPath: z.string().min(1).max(256).optional(),
    // DSR 1.2.0 — replace the tool's registered-file list without a
    // full reinstall. Persisted under settings.registryFiles (no DB
    // migration); registryPath is re-pointed at the first entry so the
    // legacy single-file read path stays coherent.
    registryFiles: z.array(RegistryFileEntry).min(1).optional(),
    registryPublic: z.boolean().optional(),
    settings: z.record(z.unknown()).optional(),
    isActive: z.boolean().optional(),
});
// CTSK-292 — PATCH /tools/:id/manifest. Body: { manifest } — the FULL
// manifest envelope, same shape the operator downloaded from
// GET /tools/:id/manifest (View → Edit → Save round-trip). The wire
// shape deliberately reuses ToolManifestSchema (which composes
// ToolStorageSchema / RegistryFileEntry / ToolDefaultsSchema /
// ToolAccessSchema / ToolActionSchema — imported, not duplicated) so
// install and manifest-edit can never drift apart.
//
// Identity fields (slug, type, storage.containerName, actions[], and
// the optional nestId/nestSlug echoes) MUST be present-and-unchanged:
// the route compares them against the stored row and rejects changes
// with 400 VALIDATION_ERROR + details[0].code ∈ {SLUG_CHANGE_FORBIDDEN,
// TYPE_CHANGE_FORBIDDEN, NEST_CHANGE_FORBIDDEN,
// CONTAINER_CHANGE_FORBIDDEN, ACTIONS_CHANGE_FORBIDDEN}. Schema-level
// validation stays shape-only; the change detection is route logic
// (apps/api/src/routes/tools.ts) because it needs the stored row.
//
// Fields that do NOT round-trip GET → edit → PATCH (Phase 0 recon,
// CTSK-292 comment 01KZ4V5KHGZEZ8JNDTEXSNQ9BB):
//   - storage.containerName — install-time find-or-create key only;
//     echoed read-only from the bound container post-install. A
//     different value is a rebind attempt → CONTAINER_CHANGE_FORBIDDEN
//     (registry_container_id is onDelete:'restrict'; rebinding would
//     orphan every previously-ingested registry document).
//   - Row-only metadata from GET /tools/:id (id, orgId, nestId,
//     nestSlug/Code/Name, registryContainerId/Name/Slug, installedAt,
//     installedBy, createdAt, updatedAt) — never part of the manifest
//     envelope; GET /tools/:id/manifest omits them entirely.
//   - nestSlug / dryRun — ToolInstallSchema-only fields, not manifest
//     fields. nestId/nestSlug are ACCEPTED here (optional) purely so a
//     pasted manifest that carries them can be change-checked (a
//     different value → NEST_CHANGE_FORBIDDEN) instead of silently
//     stripped.
//   - actions[] — round-trips on GET but is REJECTED on change in v1
//     (drives the Tool Palette contract; mid-flight edits risk
//     breaking live consumers).
//   - defaults — persisted for future reference on PATCH, but never
//     re-applied retroactively (applyDefaults only runs at install).
export const ToolManifestPatchSchema = z.object({
    manifest: z.object({
        $schema: z.literal('captain-tasko-tool').optional(),
        tool: ToolManifestSchema.shape.tool.extend({
            // Optional echoes tolerated for change DETECTION only (see above).
            nestId: z.string().min(1).max(26).optional(),
            nestSlug: z.string().min(1).max(64).optional(),
        }),
    }),
});
// POST /tools/:id/registry — replace the registry file. Body carries
// the new contents as a string (yaml / json / whatever the tool
// decided). Cap-gated on tools:write_registry.
export const ToolRegistryWriteSchema = z.object({
    contents: z.string().max(10_000_000), // 10 MB ceiling matches MAX_UPLOAD_BYTES
    contentType: z.string().min(1).max(128).optional(),
});
// CTSK-251 — POST /tools/:id/registry/append. Appends ONE entry to the
// top-level list of a registered registry YAML file (the list key is
// derived from the file's schema via topLevelKeyForSchema below).
// Distinct from ToolRegistryWriteSchema (whole-file replace, still
// stubbed): append is the high-frequency write path producers like
// tootasko-clipper use, with its own error surface (409 dedup on
// entry.id, 422 INVALID_YAML, 404 PATH_NOT_REGISTERED /
// DOCUMENT_NOT_FOUND). Cap-gated on tools:write_registry.
export const ToolRegistryAppendSchema = z.object({
    /** File path within the tool's registered registryFiles (e.g.
     *  'bookmarks.yaml'). Appending to an unregistered path 404s. */
    path: z.string().min(1).max(256),
    /** The entry object to append. Shape is schema-owned (the server
     *  does not re-validate entry fields beyond `id` string-ness for
     *  dedup) — the registry file's own schema doc is the contract. */
    entry: z.record(z.unknown()),
});
// CTSK-364 — PATCH /tools/:id/registry/:filePath/entries/:entryId.
// A partial entry: top-level fields shallow-merge over the existing
// entry; `external_refs` deep-merges one level (per-tool namespaces);
// explicit null deletes an optional field. The MERGED result is what
// re-validates against the file's registered schema server-side — the
// patch body itself is deliberately freeform beyond object-ness, the
// same division of labor as ToolRegistryAppendSchema.entry above.
export const ToolRegistryEntryPatchSchema = z.record(z.unknown())
    .refine((p) => Object.keys(p).length > 0, { message: 'patch must have at least one field' });
// CTSK-256 — POST /tools/:id/registry/clean. Operator-invoked cleaning
// of ONE registered registry YAML file (KDP §3.7 basic cleaning
// affordance). Three fixed primitives at v1:
//   - 'dedupe'    — drop entries whose `id` duplicates an earlier
//                   entry's (first occurrence wins)
//   - 'normalize' — trim + collapse internal whitespace on every
//                   string field (recursively, incl. nested objects
//                   and arrays)
//   - 'migrate'   — apply the schema's declared migration rules. NO
//                   schema declares any today (RegistryFileEntry has
//                   no migration field), so this is a documented noop
//                   (zero changes, never an error) until a
//                   bookmarks-v1 → bookmarks-v2 style rule lands.
// dryRun DEFAULTS TO TRUE — the caller must explicitly send
// `dryRun: false` to persist. Cap-gated on tools:write_registry (same
// as /registry/append). Unknown operations and an empty operations
// array both fail Zod parsing → 400 VALIDATION_ERROR via the global
// error handler.
export const REGISTRY_CLEAN_OPERATIONS = ['dedupe', 'normalize', 'migrate'];
export const ToolRegistryCleanSchema = z.object({
    /** File path within the tool's registered registryFiles (e.g.
     *  'bookmarks.yaml'). Cleaning an unregistered path 404s. */
    path: z.string().min(1).max(256),
    /** Which primitives to run. Applied in canonical order (dedupe →
     *  normalize → migrate) regardless of array order. Must name at
     *  least one operation. */
    operations: z.array(z.enum(REGISTRY_CLEAN_OPERATIONS)).min(1),
    /** Default TRUE: preview only, no bytes touched. Pass false to
     *  persist the cleaned file + emit the tool.registry_clean audit
     *  event. */
    dryRun: z.boolean().default(true),
});
/**
 * CTSK-251 — map a registry file schema key to the top-level YAML list
 * key its entries live under. Server (append endpoint) and clients
 * (tables, producers) agree through this ONE helper, so a schema can
 * never grow two competing list keys.
 *
 * Known schemas:
 *   - 'data-sources-v1'    → 'data_sources'
 *   - 'bookmarks-v1'       → 'bookmarks'
 *   - 'events-v1'          → 'events'   (Timeline Lane 1.0.0)
 *   - 'audit-timeline-v1'  → 'events'   (Timeline Lane 1.0.0)
 *   - 'timeline-marker-v1' → 'events'   (Timeline Lane 1.0.0)
 *
 * Unknown schemas return null (NOT a throw): callers decide their own
 * failure mode — the append endpoint 422s with a clear message, while
 * a rendering client can fall back to a generic table. Returning null
 * keeps the helper total and trivially testable.
 */
export function topLevelKeyForSchema(schema) {
    switch (schema) {
        case 'data-sources-v1': return 'data_sources';
        case 'bookmarks-v1': return 'bookmarks';
        case 'events-v1': return 'events';
        case 'audit-timeline-v1': return 'events';
        case 'timeline-marker-v1': return 'events';
        default: return null;
    }
}
/**
 * CTSK-308 — known LEGACY top-level list keys per schema: keys a live
 * file may still use from before the canonical key existed. The write
 * paths honor a legacy key when (and only when) the canonical key is
 * absent, and surface a warning so the operator knows to run the
 * migration (`apps/api/scripts/migrate-dsr-list-key.ts`). This compat
 * window is one release — planned removal after the migration has run
 * on every env (tracked on CTSK-308).
 *
 * Today's only entry: pre-CTSK-303 DSR files keep their list under
 * `sources` while topLevelKeyForSchema('data-sources-v1') returns
 * `data_sources` — the mismatch that let /registry/append write a
 * second competing top-level key onto a `sources`-keyed file.
 */
export function legacyTopLevelKeysForSchema(schema) {
    switch (schema) {
        case 'data-sources-v1': return ['sources'];
        default: return [];
    }
}
// FR 2026-05-31 — POST /tools/:slug/invoke. Body selects an action
// from the tool's manifest by `actionKey` and supplies the document
// context the proxy projects into the upstream payload. params is
// forwarded verbatim alongside the projected `inputs` so per-action
// extras (e.g. a chunk-size for `chunker`) flow through without
// schema changes.
export const ToolInvokeBodySchema = z.object({
    actionKey: z.string().min(1).max(64),
    documentId: z.string().min(1).max(26),
    params: z.record(z.unknown()).optional(),
    dryRun: z.boolean().default(false),
});
// CTSK-388 (Phase 2 of CTSK-385) — first real query schema for
// `GET /tools`. Previously the route read `req.query` via a raw inline
// cast (`{ nestSlug?: string; type?: string }`) with no Zod validation
// and no `deletedAt` filtering at all. This formalizes the existing
// `nestSlug`/`type` filters alongside the new includeDeleted/deletedOnly
// pair so the route can switch to `.parse()`.
export const ToolsListQuerySchema = z
    .object({
    nestSlug: z.string().optional(),
    type: z.string().optional(),
    ...deletedFilterFields,
})
    .superRefine(refineDeletedFilterMutualExclusion);
//# sourceMappingURL=tool.js.map